import { QueryClient } from '@tanstack/react-query';

/**
 * Manages cache operations across the application
 */
class CacheManager {
  private queryClient: QueryClient | null = null;
  private userDependentSegments = ['user', 'auth', 'content', 'settings'];

  /**
   * Set the React Query client
   */
  public setQueryClient(client: QueryClient): void {
    this.queryClient = client;
  }

  /**
   * Prefetch data for a route
   */
  public prefetchRouteData(path: string): void {
    if (!this.queryClient) return;

    // Prefetch the page data
    this.queryClient.prefetchQuery({
      queryKey: ['page', path],
      queryFn: async () => {
        // In a real implementation, you would fetch data from an API here
        return {
          id: path,
          path,
          data: { title: `Page data for ${path}` },
          timestamp: Date.now()
        };
      }
    });
  }

  /**
   * Invalidate a segment of the cache
   */
  public invalidateSegment(segment: string): void {
    if (!this.queryClient) return;
    this.queryClient.invalidateQueries({ queryKey: [segment] });
  }

  /**
   * Clear all cache
   */
  public clearCache(): void {
    if (!this.queryClient) return;
    this.queryClient.clear();
  }

  /**
   * Prefetch a segment of the cache
   */
  public prefetchSegment(segment: string): void {
    if (!this.queryClient) return;
    
    // Prefetch the segment data
    this.queryClient.prefetchQuery({
      queryKey: [segment],
      queryFn: async () => {
        // In a real implementation, you would fetch data from an API here
        return {
          id: segment,
          data: { title: `Segment data for ${segment}` },
          timestamp: Date.now()
        };
      }
    });
  }

  /**
   * Clear user-specific cache segments with improved logging
   */
  public clearUserSpecificCache(): void {
    if (!this.queryClient) return;
    
    console.log('CacheManager: Clearing user-specific cache segments...');
    
    // Track which segments were cleared for better logging
    const clearedSegments = [];
    
    // Clear all user-dependent segments
    this.userDependentSegments.forEach(segment => {
      this.invalidateSegment(segment);
      clearedSegments.push(segment);
    });
    
    // Also clear any page data that might contain user-specific information
    this.queryClient.invalidateQueries({ queryKey: ['page'] });
    clearedSegments.push('page');
    
    console.log(`CacheManager: Cleared ${clearedSegments.length} cache segments: ${clearedSegments.join(', ')}`);
  }
}

// Export a singleton instance
export const cacheManager = new CacheManager();
